import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import CHECKLISTS from '../data/checklists';
import { buildTitle, DEFAULT_OG_IMAGE, SITE_URL } from '../utils/seo';

export default function Checklist() {
  const structured = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Free Checklists for African Families in Germany — Sista Kyerewaa',
    description: 'Free practical checklists for African families navigating life in Germany — integration, school, job search, and citizenship.',
    url: `${SITE_URL}/checklist`,
    numberOfItems: CHECKLISTS.length,
    itemListElement: CHECKLISTS.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.title,
      url: `${SITE_URL}/checklist/${c.slug}`,
    })),
  };

  return (
    <>
      <Helmet>
        <title>{buildTitle('Free Checklists for African Families in Germany')}</title>
        <meta
          name="description"
          content="Free practical checklists for African and Ghanaian families in Germany — from your first month to school enrollment, job search, and citizenship preparation."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/checklist`} />
        <meta property="og:title" content={buildTitle('Free Checklists for African Families in Germany')} />
        <meta property="og:description" content="Free step-by-step checklists for African families navigating integration, school, jobs, and citizenship in Germany." />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={buildTitle('Free Checklists for African Families in Germany')} />
        <meta property="twitter:description" content="Free practical checklists for African families in Germany — integration, school, jobs, and citizenship." />
        <meta property="twitter:image" content={DEFAULT_OG_IMAGE} />
        <link rel="canonical" href={`${SITE_URL}/checklist`} />
        <script type="application/ld+json">{JSON.stringify(structured)}</script>
      </Helmet>

      <main className="max-w-5xl mx-auto px-6 py-16">

        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">Free resources</p>
          <h1 className="font-display text-4xl md:text-5xl mt-4 text-[#1c1b1a]">
            Checklists for African families in Germany
          </h1>
          <p className="text-[#6a6661] mt-4 max-w-2xl mx-auto">
            Practical, step-by-step checklists for every stage of your journey in Germany.
            Free for the community — written by a registered nurse and community educator who has lived it.
          </p>
        </div>

        {/* Checklist grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {CHECKLISTS.map(c => (
            <Link
              key={c.slug}
              to={`/checklist/${c.slug}`}
              className="border border-stone-200 bg-white/70 p-6 hover:shadow-md hover:border-[#8C7B6B] transition-all duration-200 group flex flex-col"
            >
              <div className="text-3xl mb-4">{c.icon}</div>
              <div className="flex flex-wrap gap-1 mb-3">
                {c.tags.slice(0, 2).map(tag => (
                  <span key={tag} className="text-[0.6rem] uppercase tracking-[0.2em] bg-[#e7e4df] text-[#2d2b29] px-2 py-0.5">
                    {tag}
                  </span>
                ))}
              </div>
              <h2 className="font-display text-xl text-[#2E4036] group-hover:text-[#1a2a20] transition-colors mb-2">
                {c.title}
              </h2>
              <p className="text-sm text-[#6a6661] flex-1">{c.subtitle}</p>
              <p className="text-xs uppercase tracking-[0.25em] text-[#CC5833] mt-4">
                Open checklist →
              </p>
            </Link>
          ))}
        </div>

        {/* Community note */}
        <div className="mt-14 border border-[#8C7B6B]/20 bg-[#2E4036] p-8 md:p-10 text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">From Sista Kyerewaa</p>
          <h2 className="font-display text-2xl md:text-3xl text-[#F2F0E9] mt-4">
            These are free — always.
          </h2>
          <p className="text-[#F2F0E9]/80 mt-4 max-w-xl mx-auto">
            When I arrived in Germany I wished someone had given me a clear list of exactly what to do.
            These checklists are what I give to every family I work with. No cost, no catch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link
              to="/blog"
              className="inline-flex items-center justify-center px-8 py-3 border border-[#F2F0E9]/30 text-[#F2F0E9] text-xs uppercase tracking-[0.25em] hover:bg-[#F2F0E9]/10 transition-colors"
            >
              Read the blog
            </Link>
            <Link
              to="/book"
              className="inline-flex items-center justify-center px-8 py-3 bg-[#F2F0E9] text-[#2E4036] text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors"
            >
              Book a session
            </Link>
          </div>
        </div>
      </main>
    </>
  );
}
